/**
 * SCRATCHPAD STORE - v4.0 (Banco de Ideias)
 *
 * Store Zustand para Banco de Ideias
 * Universal - não atrelado a projetos específicos
 * Persiste no Google Drive em /Bigwa_App/Scratchpad/notes.json
 * 
 * v4.0: Tags como metadados separados (não inline hashtags)
 *       Filtro por tag, isolamento total de contentTags/usageTags
 */

import { create } from 'zustand';
import { googleDrive } from '../services/googleDrive';
import { withOfflineGuard } from '../services/offlineQueue/offlineGuard';
import { stampSchemaArray } from '../services/googleDrive/schemaMigration';
import { logger } from '../utils/logger';
import { safeSetItem } from '../utils/safeLocalStorage';
import { driveErrorToast } from '../utils/driveErrorToast';
import { bigwaToast } from '../utils/bigwaToast';
import { auditLog } from '../utils/auditLog';
import { useFocusModeStore } from '../hooks/useFocusMode';

export interface ScratchpadNoteSentTo {
  type: 'outliner' | 'sumario' | 'quadro-comparativo';
  target: string;
}

export interface IdeaConnection {
  workId: string;
  score: number;           // 0-1, normalized keyword overlap
  matchedKeywords: string[];
}

export interface ScratchpadNote {
  id: string;
  title: string;
  content: string;
  tags: string[];
  projectId?: string | null;
  sentTo?: ScratchpadNoteSentTo | null;
  origin?: 'desktop' | 'mobile';
  isPinned?: boolean;                  // Pin intelectual (ideia ativa para exploração)
  connections?: IdeaConnection[];      // Obras da biblioteca que dialogam
  connectionsUpdatedAt?: string;       // ISO timestamp do último matching
  isDeleted?: boolean;                 // Soft-delete (tombstone para merge multi-device)
  deletedAt?: string;                  // ISO timestamp da deleção
  mesaCriadaId?: string | null;        // ID da Mesa criada a partir desta ideia
  createdAt: string;
  updatedAt: string;
}

interface ScratchpadState {
  // UI State
  isOpen: boolean;
  isPinned: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  filterTag: string | null;
  
  // Data
  notes: ScratchpadNote[];
  selectedNoteId: string | null;
  isLoading: boolean;
  isDirty: boolean;
  notesLoadedAt: number | null;
  
  // Actions - UI
  open: () => void;
  close: () => void;
  toggle: () => void;
  togglePin: () => void;
  setPosition: (x: number, y: number) => void;
  setSize: (width: number, height: number) => void;
  setFilterTag: (tag: string | null) => void;
  
  // Actions - Notes
  selectNote: (noteId: string | null) => void;
  createNote: () => void;
  createNoteFromCapture: (text: string) => void;
  createNoteFromMobile: (text: string, projectId?: string | null) => Promise<void> | void;
  updateNote: (noteId: string, updates: Partial<Pick<ScratchpadNote, 'title' | 'content' | 'tags' | 'sentTo' | 'isPinned' | 'projectId' | 'mesaCriadaId'>>) => void;
  deleteNote: (noteId: string) => void;
  
  // Actions - Pin & Connections
  toggleNotePin: (noteId: string) => void;
  setNoteConnections: (noteId: string, connections: IdeaConnection[]) => void;

  // Actions - Tags
  addTagToNote: (noteId: string, tag: string) => void;
  removeTagFromNote: (noteId: string, tag: string) => void;
  getAllTags: () => string[];
  
  // Actions - Persistence
  loadNotes: () => Promise<void>;
  saveNotes: () => Promise<void>;

  // Cross-device sync
  revalidateIfStale: () => void;
}

// Chave para localStorage (apenas posição/tamanho - dados vão pro Drive)
const SCRATCHPAD_UI_KEY = 'bigwa-scratchpad-ui';

// Posição centralizada na área útil (descontando sidebar)
const centeredPosition = (panelWidth: number, panelHeight: number) => {
  const sidebarEl = document.querySelector('.app-sidebar');
  const sidebarWidth = sidebarEl ? sidebarEl.getBoundingClientRect().width : 0;
  const availableWidth = window.innerWidth - sidebarWidth;
  return {
    x: Math.max(0, sidebarWidth + Math.round((availableWidth - panelWidth) / 2)),
    y: Math.max(0, Math.round((window.innerHeight - panelHeight) / 2))
  };
};

const DEFAULT_SIZE = { width: 400, height: 520 };

// Debounce timer reference
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// Cross-device sync: timestamp of last local mutation (Drive write)
let _lastMutationAt = 0;

// Carregar configurações de UI do localStorage
function loadUIState() {
  try {
    const saved = localStorage.getItem(SCRATCHPAD_UI_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        position: parsed.position || centeredPosition(DEFAULT_SIZE.width, DEFAULT_SIZE.height),
        size: parsed.size || DEFAULT_SIZE,
        isPinned: parsed.isPinned || false
      };
    }
  } catch (e) {
    logger.error('Scratchpad', 'Erro ao carregar UI:', e);
  }
  return {
    position: centeredPosition(DEFAULT_SIZE.width, DEFAULT_SIZE.height),
    size: DEFAULT_SIZE,
    isPinned: false
  };
}

// Salvar configurações de UI no localStorage
function saveUIState(
  position: { x: number; y: number }, 
  size: { width: number; height: number },
  isPinned: boolean
) {
  try {
    safeSetItem(SCRATCHPAD_UI_KEY, JSON.stringify({ position, size, isPinned }));
  } catch (e) {
    logger.error('Scratchpad', 'Erro ao salvar UI:', e);
  }
}

// Migrar notas antigas que podem ter hashtags no content
function migrateNote(note: ScratchpadNote | Record<string, unknown>): ScratchpadNote {
  const migrated: ScratchpadNote = {
    id: note.id as string,
    title: (note.title as string) || 'Sem título',
    content: (note.content as string) || '',
    tags: Array.isArray(note.tags) ? note.tags : [],
    projectId: (note.projectId as string) || null,
    sentTo: (note.sentTo as ScratchpadNoteSentTo) || null,
    origin: (note.origin as ScratchpadNote['origin']) || 'desktop',
    isPinned: (note.isPinned as boolean) ?? false,
    connections: Array.isArray(note.connections) ? note.connections as IdeaConnection[] : [],
    connectionsUpdatedAt: (note.connectionsUpdatedAt as string) || undefined,
    isDeleted: (note.isDeleted as boolean) ?? false,
    deletedAt: (note.deletedAt as string) || undefined,
    mesaCriadaId: (note.mesaCriadaId as string) || null,
    createdAt: (note.createdAt as string) || new Date().toISOString(),
    updatedAt: (note.updatedAt as string) || new Date().toISOString()
  };
  
  // Se não tem tags mas tem hashtags no content, extrair
  if (migrated.tags.length === 0 && migrated.content.includes('#')) {
    const hashtagRegex = /#([\w\-àáâãéèêíïóôõúüçÀÁÂÃÉÈÊÍÏÓÔÕÚÜÇ]+)/gi;
    let match;
    while ((match = hashtagRegex.exec(migrated.content)) !== null) {
      const tag = match[1].toLowerCase();
      if (!migrated.tags.includes(tag)) {
        migrated.tags.push(tag);
      }
    }
    // Remover hashtags do content
    if (migrated.tags.length > 0) {
      migrated.content = migrated.content
        .replace(/#[\w\-àáâãéèêíïóôõúüçÀÁÂÃÉÈÊÍÏÓÔÕÚÜÇ]+/gi, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    }
  }
  
  return migrated;
}

export const useScratchpadStore = create<ScratchpadState>((set, get) => {
  const uiState = loadUIState();

  // Rollback: captura do estado antes da primeira mutação desde o último save
  let _rollbackNotes: ScratchpadNote[] | null = null;
  let _rollbackSelectedNoteId: string | null = null;

  // Guard: última contagem conhecida de notas carregadas do Drive (Guard C)
  let _lastKnownCount = 0;

  /** Captura snapshot para rollback se ainda não foi capturado neste ciclo dirty */
  const captureRollback = () => {
    if (!get().isDirty) {
      _rollbackNotes = get().notes;
      _rollbackSelectedNoteId = get().selectedNoteId;
    }
  };

  return {
    // Initial UI State
    isOpen: false,
    isPinned: uiState.isPinned,
    position: uiState.position,
    size: uiState.size,
    filterTag: null,
    
    // Initial Data
    notes: [],
    selectedNoteId: null,
    isLoading: false,
    isDirty: false,
    notesLoadedAt: null,
    
    // ==================== UI Actions ====================
    
    open: () => {
      const { notes, isLoading, size } = get();

      // Focus mode imediato (antes do lazy load do componente)
      useFocusModeStore.getState().enterFocusMode();

      // Centralizar na tela cheia (sidebar será colapsada pelo focus mode)
      set({
        isOpen: true,
        position: {
          x: Math.max(0, Math.round((window.innerWidth - size.width) / 2)),
          y: Math.max(0, Math.round((window.innerHeight - size.height) / 2))
        }
      });

      if (notes.length === 0 && !isLoading) {
        get().loadNotes();
      }
    },

    close: () => {
      useFocusModeStore.getState().exitFocusMode();
      set({ isOpen: false });
    },
    
    toggle: () => {
      const { isOpen } = get();
      if (isOpen) {
        get().close();
      } else {
        get().open();
      }
    },
    
    togglePin: () => {
      const { isPinned, position, size } = get();
      const newPinned = !isPinned;
      set({ isPinned: newPinned });
      saveUIState(position, size, newPinned);
    },
    
    setPosition: (x, y) => {
      const { size, isPinned } = get();
      set({ position: { x, y } });
      saveUIState({ x, y }, size, isPinned);
    },
    
    setSize: (width, height) => {
      const { position, isPinned } = get();
      set({ size: { width, height } });
      saveUIState(position, { width, height }, isPinned);
    },
    
    setFilterTag: (tag) => set({ filterTag: tag }),
    
    // ==================== Note Actions ====================
    
    selectNote: (noteId) => set({ selectedNoteId: noteId }),
    
    createNote: () => {
      captureRollback();
      const newNote: ScratchpadNote = {
        id: crypto.randomUUID(),
        title: 'Nova ideia',
        content: '',
        tags: [],
        origin: 'desktop',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      set(state => ({
        notes: [newNote, ...state.notes],
        selectedNoteId: newNote.id,
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      get().saveNotes();
    },

    createNoteFromCapture: (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      captureRollback();
      const lines = trimmed.split('\n').filter(l => l.trim());
      const title = (lines[0] || '').slice(0, 60);

      const newNote: ScratchpadNote = {
        id: crypto.randomUUID(),
        title,
        content: trimmed,
        tags: [],
        origin: 'desktop',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      set(state => ({
        notes: [newNote, ...state.notes],
        selectedNoteId: newNote.id,
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      get().saveNotes();
    },

    createNoteFromMobile: async (text: string, projectId?: string | null) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // GUARD B: Garantir que notes foram carregadas antes de criar
      if (!get().notesLoadedAt) {
        await get().loadNotes();
        // Se load falhou e notesLoadedAt ainda é null, bloquear
        if (!get().notesLoadedAt) {
          logger.error('[scratchpadStore.createNoteFromMobile]', 'loadNotes falhou — criação bloqueada');
          bigwaToast.error('Não foi possível carregar notas. Tente novamente.');
          return;
        }
      }

      captureRollback();
      const lines = trimmed.split('\n').filter(l => l.trim());
      const title = (lines[0] || '').slice(0, 60);

      const newNote: ScratchpadNote = {
        id: crypto.randomUUID(),
        title,
        content: trimmed.slice(0, 500_000),
        tags: [],
        projectId: projectId || null,
        origin: 'mobile',
        isPinned: false,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      set(state => ({
        notes: [newNote, ...state.notes],
        selectedNoteId: newNote.id,
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      get().saveNotes();
    },

    updateNote: (noteId, updates) => {
      captureRollback();
      // Limitar content a 500K chars (~1MB) para proteger IndexedDB e Drive
      const safeUpdates = updates.content && updates.content.length > 500_000
        ? { ...updates, content: updates.content.slice(0, 500_000) }
        : updates;
      set(state => ({
        notes: state.notes.map(note =>
          note.id === noteId
            ? { ...note, ...safeUpdates, updatedAt: new Date().toISOString() }
            : note
        ),
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer);
      }
      saveDebounceTimer = setTimeout(() => {
        get().saveNotes();
      }, 1000);
    },
    
    deleteNote: (noteId) => {
      captureRollback();
      const { notes, selectedNoteId } = get();
      const noteIndex = notes.findIndex(n => n.id === noteId && !n.isDeleted);
      const now = new Date().toISOString();

      // Soft-delete: setar flags em vez de remover do array (tombstone para merge multi-device)
      const newNotes = notes.map(n =>
        n.id === noteId ? { ...n, isDeleted: true, deletedAt: now, updatedAt: now } : n
      );
      const visibleNotes = newNotes.filter(n => !n.isDeleted);

      let newSelectedId: string | null = null;
      if (selectedNoteId === noteId && visibleNotes.length > 0) {
        const newIndex = Math.min(noteIndex, visibleNotes.length - 1);
        newSelectedId = visibleNotes[newIndex]?.id || null;
      } else if (selectedNoteId !== noteId) {
        newSelectedId = selectedNoteId;
      }

      set({
        notes: newNotes,
        selectedNoteId: newSelectedId,
        isDirty: true
      });
      _lastMutationAt = Date.now();

      get().saveNotes();
    },
    
    // ==================== Pin & Connections ====================

    toggleNotePin: (noteId) => {
      captureRollback();
      set(state => ({
        notes: state.notes.map(note =>
          note.id === noteId
            ? { ...note, isPinned: !note.isPinned, updatedAt: new Date().toISOString() }
            : note
        ),
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
      saveDebounceTimer = setTimeout(() => get().saveNotes(), 1000);
    },

    setNoteConnections: (noteId, connections) => {
      set(state => ({
        notes: state.notes.map(note =>
          note.id === noteId
            ? { ...note, connections, connectionsUpdatedAt: new Date().toISOString() }
            : note
        ),
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
      saveDebounceTimer = setTimeout(() => get().saveNotes(), 1000);
    },

    // ==================== Tag Actions ====================

    addTagToNote: (noteId, tag) => {
      const normalizedTag = tag.toLowerCase().trim().slice(0, 100);
      if (!normalizedTag) return;
      captureRollback();
      set(state => ({
        notes: state.notes.map(note => {
          if (note.id !== noteId) return note;
          if (note.tags.includes(normalizedTag)) return note;
          return {
            ...note,
            tags: [...note.tags, normalizedTag],
            updatedAt: new Date().toISOString()
          };
        }),
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
      saveDebounceTimer = setTimeout(() => get().saveNotes(), 1000);
    },
    
    removeTagFromNote: (noteId, tag) => {
      captureRollback();
      set(state => ({
        notes: state.notes.map(note => {
          if (note.id !== noteId) return note;
          return {
            ...note,
            tags: note.tags.filter(t => t !== tag),
            updatedAt: new Date().toISOString()
          };
        }),
        isDirty: true
      }));
      _lastMutationAt = Date.now();

      if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
      saveDebounceTimer = setTimeout(() => get().saveNotes(), 1000);
    },
    
    getAllTags: () => {
      const { notes } = get();
      const tags = new Set<string>();
      notes.forEach(n => n.tags.forEach(t => tags.add(t)));
      return Array.from(tags).sort();
    },
    
    // ==================== Persistence Actions ====================
    
    loadNotes: async () => {
      const { isLoading } = get();
      if (isLoading) return;
      
      set({ isLoading: true });
      
      try {
        
        const rawNotes = await googleDrive.loadScratchpadNotes();
        
        if (rawNotes && Array.isArray(rawNotes) && rawNotes.length > 0) {
          let notes = rawNotes.map(migrateNote);

          // Purge notas soft-deleted há mais de 30 dias
          const PURGE_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;
          const beforePurge = notes.length;
          notes = notes.filter(n => {
            if (!n.isDeleted || !n.deletedAt) return true;
            return Date.now() - new Date(n.deletedAt).getTime() < PURGE_THRESHOLD_MS;
          });
          const purged = beforePurge > notes.length;

          // Check if migration changed anything
          const migrated = JSON.stringify(rawNotes) !== JSON.stringify(notes) || purged;

          const visibleNotes = notes.filter(n => !n.isDeleted);
          _lastKnownCount = notes.length;
          set({
            notes,
            selectedNoteId: visibleNotes[0]?.id || null,
            isLoading: false,
            isDirty: migrated,
            notesLoadedAt: Date.now()
          });
          
          if (migrated) {
            setTimeout(() => get().saveNotes(), 500);
          }
          
        } else {
          set({
            notes: [],
            selectedNoteId: null,
            isLoading: false,
            isDirty: false,
            notesLoadedAt: Date.now()
          });
        }
        
      } catch (e) {
        logger.error('Scratchpad', 'Erro ao carregar notas:', e);
        set({ isLoading: false });
        
        try {
          const localData = localStorage.getItem('bigwa-scratchpad-notes');
          if (localData) {
            const localNotes = JSON.parse(localData);
            if (Array.isArray(localNotes) && localNotes.length > 0) {
              const migrated = localNotes.map(migrateNote);
              set({
                notes: migrated,
                selectedNoteId: migrated[0].id,
                isDirty: true
              });
              setTimeout(() => get().saveNotes(), 500);
            }
          }
        } catch (localError) {
          logger.error('Scratchpad', 'Erro no fallback localStorage:', localError);
        }
      }
    },
    
    revalidateIfStale: () => {
      const { notesLoadedAt, isLoading } = get();
      if (!notesLoadedAt || isLoading) return;
      if (Date.now() - notesLoadedAt < 300_000) return;
      // Local mutation within last 10s — DriveSync may not have flushed yet
      if (_lastMutationAt && Date.now() - _lastMutationAt < 10_000) return;
      get().loadNotes();
    },

    saveNotes: async () => {
      const { notes, isDirty, notesLoadedAt } = get();

      if (!isDirty) {
        return;
      }

      // GUARD A: Bloquear save se loadNotes nunca completou nesta sessão
      if (!notesLoadedAt) {
        logger.error('[scratchpadStore.saveNotes]', 'Tentativa de save sem load prévio — BLOQUEADO');
        return;
      }

      // GUARD C: Bloquear se array encolheu >50% vs última contagem conhecida
      if (_lastKnownCount > 0 && notes.length < _lastKnownCount * 0.5) {
        logger.error('[scratchpadStore.saveNotes]', `Save bloqueado: notes.length=${notes.length} vs lastKnown=${_lastKnownCount} — possível perda de dados`);
        return;
      }

      try {

        // stampSchema ANTES do enqueue para garantir schemaVersion mesmo offline
        const stamped = stampSchemaArray(notes);
        const result = await withOfflineGuard(
          'scratchpadNotes',
          JSON.stringify(stamped),
          () => googleDrive.saveScratchpadNotes(notes),
        );

        if (result.queued) {
          // Enfileirado offline — manter state, não limpar dirty
          return;
        }

        set({ isDirty: false });
        _lastKnownCount = notes.length;
        auditLog({ operation: 'update', target: 'scratchpad', detail: `Notas salvas (${notes.length} notas)`, success: true });
        // Save bem-sucedido — limpar rollback
        _rollbackNotes = null;
        _rollbackSelectedNoteId = null;

        try {
          localStorage.removeItem('bigwa-scratchpad-notes');
        } catch (_e) {
          // Ignorar
        }

      } catch (e) {
        logger.error('[scratchpadStore.saveNotes]', e);
        driveErrorToast(e, 'Não foi possível salvar as notas. Tente novamente.');
        auditLog({ operation: 'update', target: 'scratchpad', detail: 'Falha ao salvar notas', success: false, error: String(e) });

        // Fallback localStorage (camada extra)
        try {
          safeSetItem('bigwa-scratchpad-notes', JSON.stringify(notes));
        } catch (localError) {
          logger.error('Scratchpad', 'Erro no backup localStorage:', localError);
          // localStorage também falhou — rollback do state
          if (_rollbackNotes) {
            set({
              notes: _rollbackNotes,
              selectedNoteId: _rollbackSelectedNoteId,
              isDirty: false
            });
            _rollbackNotes = null;
            _rollbackSelectedNoteId = null;
          }
        }
      }
    }
  };
});
